import {
  List, Datagrid, TextField, EmailField, DateField, EditButton, Edit, SimpleForm,
  TextInput, SelectInput, ExportButton, TopToolbar, FilterButton, useGetList,
  useRecordContext,
} from 'react-admin';
import { Stack, Box, Chip, Typography } from '@mui/material';
import InboxIcon from '@mui/icons-material/MoveToInbox';

const STATUSES = [
  { id: 'new', name: 'New' },
  { id: 'contacted', name: 'Contacted' },
  { id: 'closed', name: 'Closed' },
];

// Delivery outcome of the notification email (written by the API after sending).
const NOTIFY = {
  sent: { label: 'Emailed', color: 'success' },
  failed: { label: 'Email failed', color: 'error' },
  skipped: { label: 'Email off', color: 'default' },
  pending: { label: 'Sending…', color: 'default' },
};

const ClassFilter = (props) => {
  const { data } = useGetList('admission-classes', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'sortOrder', order: 'ASC' },
  });
  return <SelectInput {...props} choices={data || []} optionText="name" />;
};

const filters = [
  <TextInput key="q" source="q" label="Search name/email" alwaysOn />,
  <SelectInput key="status" source="status" choices={STATUSES} alwaysOn />,
  <ClassFilter key="admissionClassId" source="admissionClassId" label="Class" alwaysOn />,
];

const StatusChip = () => {
  const value = useRecordContext()?.status;
  const color = value === 'new' ? 'warning' : value === 'contacted' ? 'info' : 'default';
  return <Chip size="small" label={value} color={color} />;
};

const NotifyChip = () => {
  const record = useRecordContext();
  const n = NOTIFY[record?.notifyStatus];
  if (!n) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return <Chip size="small" variant="outlined" label={n.label} color={n.color} title={record.notifiedTo || ''} />;
};

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

const EnquiryList = () => (
  <List
    sort={{ field: 'createdAt', order: 'DESC' }}
    filters={filters}
    actions={<ListActions />}
    perPage={25}
    filterDefaultValues={{ status: 'new' }}
  >
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <DateField source="createdAt" label="Received" showTime />
      <TextField source="parentName" label="Parent" />
      <TextField source="phone" />
      <EmailField source="email" />
      <TextField source="applyingFor" label="Applying for" />
      <Box label="Status">
        <StatusChip />
      </Box>
      <Box label="Notification">
        <NotifyChip />
      </Box>
      <EditButton />
    </Datagrid>
  </List>
);

const NotifySummary = () => {
  const record = useRecordContext();
  if (!record?.notifyStatus) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <NotifyChip />
      <Typography variant="body2" color="text.secondary">
        {record.notifiedTo ? `to ${record.notifiedTo}` : 'no recipient configured'}
      </Typography>
    </Stack>
  );
};

const EnquiryEdit = () => (
  <Edit redirect="list" mutationMode="pessimistic">
    <SimpleForm>
      <Stack spacing={2} sx={{ maxWidth: 720 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextInput source="parentName" label="Parent" fullWidth disabled />
          <TextInput source="childAge" label="Child age" disabled />
          <TextInput source="applyingFor" label="Applying for" fullWidth disabled />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextInput source="phone" disabled fullWidth />
          <TextInput source="email" disabled fullWidth />
        </Stack>
        <TextInput source="message" multiline minRows={4} fullWidth disabled />
        <NotifySummary />
        <SelectInput source="status" choices={STATUSES} label="Update status" />
      </Stack>
    </SimpleForm>
  </Edit>
);

export default {
  list: EnquiryList,
  edit: EnquiryEdit,
  icon: InboxIcon,
  options: { label: 'Admission enquiries' },
  recordRepresentation: 'parentName',
};
